<template>
    <p ref="textBoxRef" class="ellipsis-text">
        {{ displayText }}
        <slot v-if="offsetText" name="tail">{{ offsetText }}</slot>
        <template v-if="showMore && isOverflow">
            <span :style="toggleButtonStyle" @click="toggleExpand">
                {{ isExpanded ? collapseText : moreText }}
            </span>
        </template>
    </p>
</template>

<script setup lang="ts">
    import {
        computed,
        CSSProperties,
        onBeforeUnmount,
        onMounted,
        ref,
        watch,
    } from 'vue';

    // ----- props -----
    const props = withDefaults(
        defineProps<{
            text: string;
            limitedLine?: number;
            showMore?: boolean;
            moreText?: string;
            collapseText?: string;
            offsetText?: string;
            expandable?: boolean;
        }>(),
        {
            limitedLine: 3,
            showMore: false,
            moreText: '(查看更多)',
            collapseText: '(收起內容)',
            offsetText: '',
            expandable: false,
        }
    );

    // ----- emits -----
    const emits = defineEmits<{
        (event: 'click', payload: MouseEvent): void;
        (event: 'toggle', payload: boolean): void;
    }>();

    // ----- state -----
    const textBoxRef = ref<HTMLElement | null>(null);
    const truncatedText = ref('');
    const isOverflow = ref(false);
    const isExpanded = ref(false);
    const resizeObserverRef = ref<ResizeObserver | null>(null);

    // ----- computed -----
    const cleanText = computed(() => {
        return props.text.replace(/\s+/g, ' ').trim();
    });

    const displayText = computed(() => {
        return isExpanded.value ? cleanText.value : truncatedText.value;
    });

    const toggleButtonStyle = computed<CSSProperties>(() => {
        return {
            color: '#888888',
            cursor: 'pointer',
        };
    });

    // ----- methods -----
    // 計算實際文字寬度
    const measureTextWidth = (text: string): number => {
        const textBox = textBoxRef.value;
        if (!textBox) return 0;

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) return 0;

        context.font = window.getComputedStyle(textBox).font || '16px Arial';
        return context.measureText(text).width;
    };

    // 截斷文字處理
    const truncateText = (): void => {
        truncatedText.value = '';
        isOverflow.value = false;

        const textBox = textBoxRef.value;
        if (!textBox) return;

        const containerWidth = textBox.offsetWidth;
        const maxWidth = containerWidth * props.limitedLine;

        const threeDots = '...';
        const ellipsisWidth = measureTextWidth(`\t${threeDots}\t`);
        const showMoreWidth = measureTextWidth(
            props.showMore ? props.moreText : ''
        );
        const offsetTextWidth = measureTextWidth(props.offsetText);
        const extraWidth = measureTextWidth('\t預留\t');
        const reservedWidth =
            ellipsisWidth + offsetTextWidth + showMoreWidth + extraWidth;

        let currentWidth = 0;
        let currentTruncatedText = '';

        for (let i = 0; i < cleanText.value.length; i += 1) {
            const char = cleanText.value[i];
            const charWidth = measureTextWidth(char);

            if (currentWidth + charWidth > maxWidth - reservedWidth) {
                currentTruncatedText += threeDots;
                isOverflow.value = true;
                break;
            }

            currentTruncatedText += char;
            currentWidth += charWidth;
        }

        truncatedText.value = currentTruncatedText;
    };

    // 點擊事件
    const toggleExpand = (event: MouseEvent): void => {
        if (!isExpanded.value) {
            emits('click', event);
        }

        if (props.expandable) {
            isExpanded.value = !isExpanded.value;
            emits('toggle', isExpanded.value);
        }
    };

    // ----- watch -----
    watch(
        () => props.text,
        () => {
            truncateText();
        }
    );

    // ----- lifecycle -----
    onMounted(() => {
        resizeObserverRef.value = new ResizeObserver(() => {
            truncateText();
        });

        if (textBoxRef.value) {
            resizeObserverRef.value.observe(textBoxRef.value);
        }

        truncateText();
    });

    onBeforeUnmount(() => {
        if (resizeObserverRef.value) {
            if (textBoxRef.value) {
                resizeObserverRef.value.unobserve(textBoxRef.value);
            }
            resizeObserverRef.value.disconnect();
            resizeObserverRef.value = null;
        }
    });
</script>

<style scoped lang="scss">
    .ellipsis-text {
        margin-top: 0;
        word-break: break-all;
        text-align: start;
    }
</style>
